// Unified admin page. Hosts the four admin tabs (Overview / Usage /
// Errors / Users) under a single top nav, with one back-to-app
// affordance and a single state-machine entry point in App.tsx.
//
// Why a shell vs. four separate full-page views: each tab owns its
// own filters / state, and we want the user to switch between them
// without bouncing through the main app. Embedded tabs also let the
// Overview page link directly into Usage / Errors.

import { useEffect, useState } from 'react'
import { AdminBilling } from './AdminBilling'
import { AdminErrors } from './AdminErrors'
import { AdminInvites } from './AdminInvites'
import { AdminOverviewView } from './AdminOverview'
import { AdminSandbox } from './AdminSandbox'
import { AdminUsage } from './AdminUsage'
import { AdminUsersView } from './AdminUsers'

export interface AdminShellProps {
  /** Back-to-app handler — owned by the parent (App.tsx) so it can
   *  reset its `view` state. */
  onBack: () => void
}

type Tab = 'overview' | 'usage' | 'errors' | 'sandbox' | 'users' | 'billing' | 'invites'

const TABS: ReadonlyArray<readonly [Tab, string]> = [
  ['overview', 'Overview'],
  ['usage', 'Usage'],
  ['billing', 'Billing'],
  ['invites', 'Invites'],
  ['errors', 'Errors'],
  ['sandbox', 'Sandbox'],
  ['users', 'Users'],
]

const VALID_TABS: ReadonlySet<Tab> = new Set([
  'overview',
  'usage',
  'errors',
  'sandbox',
  'users',
  'billing',
  'invites',
])

export function AdminShell({ onBack }: AdminShellProps) {
  // Mirrored to sessionStorage so a refresh on Usage / Errors / Users
  // doesn't kick the user back to Overview.
  const [tab, setTab] = useState<Tab>(() => {
    try {
      const v = sessionStorage.getItem('draftlize:admin.tab')
      return v && VALID_TABS.has(v as Tab) ? (v as Tab) : 'overview'
    } catch {
      return 'overview'
    }
  })
  useEffect(() => {
    try {
      sessionStorage.setItem('draftlize:admin.tab', tab)
    } catch {}
  }, [tab])

  return (
    <div
      style={{
        height: '100vh',
        overflow: 'auto',
        background: 'var(--paper-0)',
        color: 'var(--ink-1)',
      }}
    >
      {/* Header: back + title + tab nav */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'var(--paper-0)',
          borderBottom: '1px solid var(--rule-s)',
          padding: '14px 32px 0',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            marginBottom: 10,
          }}
        >
          <button
            type="button"
            onClick={onBack}
            style={{
              background: 'transparent',
              border: '1px solid var(--rule-s)',
              color: 'var(--ink-2)',
              padding: '4px 10px',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 12,
            }}
          >
            ← back
          </button>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Admin</h1>
        </div>
        <nav style={{ display: 'flex', gap: 4 }}>
          {TABS.map(([id, label]) => {
            const active = tab === id
            return (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  borderBottom: active
                    ? '2px solid var(--ink-1)'
                    : '2px solid transparent',
                  color: active ? 'var(--ink-1)' : 'var(--ink-3)',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: active ? 600 : 400,
                  marginBottom: -1,
                }}
              >
                {label}
              </button>
            )
          })}
        </nav>
      </header>

      {/* Tab body */}
      <main style={{ padding: '20px 32px 40px' }}>
        {tab === 'overview' && (
          <AdminOverviewView
            onSeeUsage={() => setTab('usage')}
            onSeeErrors={() => setTab('errors')}
          />
        )}
        {tab === 'usage' && <AdminUsage />}
        {tab === 'billing' && <AdminBilling />}
        {tab === 'invites' && <AdminInvites />}
        {tab === 'errors' && <AdminErrors />}
        {tab === 'sandbox' && <AdminSandbox />}
        {tab === 'users' && <AdminUsersView />}
      </main>
    </div>
  )
}
